<script setup>
import { computed, markRaw, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { VueFlow, useVueFlow, Position } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NodePalette from './components/NodePalette.vue';
import NodePropertiesPanel from './components/NodePropertiesPanel.vue';
import StartNode from './components/nodes/StartNode.vue';
import MessageNode from './components/nodes/MessageNode.vue';
import QuestionNode from './components/nodes/QuestionNode.vue';
import ConditionNode from './components/nodes/ConditionNode.vue';
import HubsoftFaturaNode from './components/nodes/HubsoftFaturaNode.vue';
import HubsoftPlanosNode from './components/nodes/HubsoftPlanosNode.vue';
import TransferHumanNode from './components/nodes/TransferHumanNode.vue';
import AskJhulieNode from './components/nodes/AskJhulieNode.vue';
import IdentifyIntentNode from './components/nodes/IdentifyIntentNode.vue';
import HttpRequestNode from './components/nodes/HttpRequestNode.vue';
import SubFlowNode from './components/nodes/SubFlowNode.vue';
import DelayNode from './components/nodes/DelayNode.vue';
import EndNode from './components/nodes/EndNode.vue';
import { NODE_TYPES, generateNodeId, validateFlowGraph } from './constants';

const nodeTypes = {
  start: markRaw(StartNode),
  message: markRaw(MessageNode),
  question: markRaw(QuestionNode),
  condition: markRaw(ConditionNode),
  hubsoft_consultar_fatura: markRaw(HubsoftFaturaNode),
  hubsoft_consultar_planos: markRaw(HubsoftPlanosNode),
  transfer_human: markRaw(TransferHumanNode),
  ask_jhulie: markRaw(AskJhulieNode),
  identify_intent: markRaw(IdentifyIntentNode),
  http_request: markRaw(HttpRequestNode),
  sub_flow: markRaw(SubFlowNode),
  delay: markRaw(DelayNode),
  end: markRaw(EndNode),
};

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const { accountScopedRoute } = useAccount();

const {
  onConnect,
  addEdges,
  screenToFlowCoordinate,
  findNode,
  updateNodeData,
} = useVueFlow();

const isNew = computed(() => route.name === 'bot_flows_new');
const botFlowId = computed(() => route.params.botFlowId);

const inboxes = computed(() => getters['inboxes/getInboxes'].value);
const teams = computed(() => getters['teams/getTeams'].value);
const uiFlags = computed(() => getters['botFlows/getUIFlags'].value);
const otherFlows = computed(() =>
  getters['botFlows/getBotFlows'].value.filter(f => f.id !== Number(botFlowId.value))
);

const name = ref('');
const inboxId = ref(null);
const nodes = ref([{ id: 'start', type: 'start', position: { x: 40, y: 160 }, data: {} }]);
const edges = ref([]);
const selectedNodeId = ref(null);
const isLoaded = ref(false);

const selectedNode = computed(() => nodes.value.find(n => n.id === selectedNodeId.value) || null);

const withDefaultPositions = rawNodes => {
  return rawNodes.map((n, index) => ({
    ...n,
    position: n.position || { x: (index % 4) * 280 + 40, y: Math.floor(index / 4) * 180 + 40 },
  }));
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
  store.dispatch('botFlows/get');
  if (!isNew.value) {
    const flow = await store.dispatch('botFlows/show', botFlowId.value);
    if (flow) {
      name.value = flow.name;
      inboxId.value = flow.inbox_id;
      const graph = flow.flow_graph || {};
      nodes.value = withDefaultPositions(graph.nodes && graph.nodes.length ? graph.nodes : nodes.value);
      edges.value = graph.edges || [];
    }
  }
  isLoaded.value = true;
});

onConnect(connection => addEdges([connection]));

const onDragOver = event => {
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.dataTransfer.dropEffect = 'move';
};

const onDrop = event => {
  const type = event.dataTransfer.getData('application/vueflow');
  if (!type || !NODE_TYPES[type]) return;

  const position = screenToFlowCoordinate({ x: event.clientX, y: event.clientY });
  const id = generateNodeId();
  nodes.value = [...nodes.value, { id, type, position, data: NODE_TYPES[type].defaultData() }];
  selectedNodeId.value = id;
};

const onNodeClick = ({ node }) => {
  selectedNodeId.value = node.id;
};

const onPaneClick = () => {
  selectedNodeId.value = null;
};

const updateSelectedNodeData = data => {
  if (!selectedNode.value) return;
  updateNodeData(selectedNode.value.id, data);
};

const deleteSelectedNode = () => {
  if (!selectedNode.value || selectedNode.value.type === 'start') return;
  const idToRemove = selectedNode.value.id;
  nodes.value = nodes.value.filter(n => n.id !== idToRemove);
  edges.value = edges.value.filter(e => e.source !== idToRemove && e.target !== idToRemove);
  selectedNodeId.value = null;
};

const goToList = () => router.push(accountScopedRoute('bot_flows_list'));

const buildFlowGraph = () => ({
  nodes: nodes.value.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
  edges: edges.value.map(e => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle || null,
  })),
});

const save = async () => {
  if (!name.value.trim()) {
    useAlert(t('BOT_FLOWS.EDITOR.NAME_REQUIRED'));
    return;
  }

  const payload = { name: name.value, inbox_id: inboxId.value, flow_graph: buildFlowGraph() };
  const issues = validateFlowGraph(nodes.value, edges.value);
  const successMessage = issues.length
    ? `${t('BOT_FLOWS.EDITOR.SAVE_SUCCESS')} ${t('BOT_FLOWS.EDITOR.VALIDATION_WARNING')}: ${issues.join(' ')}`
    : t('BOT_FLOWS.EDITOR.SAVE_SUCCESS');

  if (isNew.value) {
    const created = await store.dispatch('botFlows/create', payload);
    if (created) {
      useAlert(successMessage);
      router.replace(accountScopedRoute('bot_flows_edit', { botFlowId: created.id }));
    } else {
      useAlert(t('BOT_FLOWS.EDITOR.SAVE_ERROR'));
    }
  } else {
    const updated = await store.dispatch('botFlows/update', { id: botFlowId.value, ...payload });
    useAlert(updated ? successMessage : t('BOT_FLOWS.EDITOR.SAVE_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-[calc(100vh-6rem)]">
    <div class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
      <Input v-model="name" :placeholder="t('BOT_FLOWS.EDITOR.NAME_LABEL')" class="max-w-xs !mb-0" />
      <select
        v-model="inboxId"
        class="reset-base !h-9 !mb-0 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
      >
        <option :value="null">{{ t('BOT_FLOWS.EDITOR.NO_INBOX') }}</option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
      <div class="flex-1" />
      <Button variant="faded" color="slate" :label="t('BOT_FLOWS.EDITOR.CANCEL')" @click="goToList" />
      <Button
        :label="t('BOT_FLOWS.EDITOR.SAVE')"
        :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
        @click="save"
      />
    </div>

    <div class="flex flex-1 min-h-0">
      <NodePalette />

      <div class="relative flex-1" @dragover="onDragOver" @drop="onDrop">
        <VueFlow
          v-if="isLoaded"
          v-model:nodes="nodes"
          v-model:edges="edges"
          :node-types="nodeTypes"
          :default-viewport="{ zoom: 1 }"
          :min-zoom="0.3"
          :max-zoom="1.5"
          @node-click="onNodeClick"
          @pane-click="onPaneClick"
        >
          <Background :gap="16" pattern-color="var(--border, #cbd5e1)" />
          <Controls />
          <MiniMap pannable zoomable />
        </VueFlow>
      </div>

      <NodePropertiesPanel
        :node="selectedNode"
        :teams="teams"
        :flows="otherFlows"
        @update="updateSelectedNodeData"
        @delete="deleteSelectedNode"
        @close="selectedNodeId = null"
      />
    </div>
  </div>
</template>

<style>
/* SmartNet: cores minimas das arestas/handles do Vue Flow no tema do app -
   o CSS padrao da lib assume fundo claro fixo. */
.vue-flow__edge-path {
  stroke: var(--border, #94a3b8);
  stroke-width: 2;
}
.vue-flow__handle {
  width: 10px;
  height: 10px;
  background: #64748b;
  border: 2px solid white;
}
</style>
